// ═══════════════════════════════════════════════════════════════
// AGENCIAS · Vista de lanzamiento — contenido (content.js)
// Lo que dice la pantalla que anuncia TikControl Agencies: fecha, textos,
// tarjetas y el bloque de «Agenda tu llamada». Todo sale de aquí «de
// fábrica» y se puede cambiar en el backoffice (Agencias → Lanzamiento),
// que lo guarda en el Worker (D1 app_config['agency_launch']).
//
// Idiomas: el backoffice edita español e inglés. Un texto sin tocar sale en
// el idioma de la app (claves agencies_launch_* de los 14 locales); uno
// cambiado sale tal cual en español y, para el resto de idiomas, en inglés
// (si el inglés se dejó vacío, su traducción de fábrica).
//
// Fuente única: lo cargan la app (launch/data.js y view.js), el editor del
// backoffice (backoffice/src/lib/agencyLaunch.js lo importa tal cual, con
// la misma vista previa) y el Worker (cf-worker/src/lib/agencyLaunch.js,
// que limpia con sanitize() lo que guarda). Sin DOM.
// Expone window.AgenciasLaunchContent.
// TikControl © 2024-2026
// ═══════════════════════════════════════════════════════════════

(function (root) {
  'use strict';

  var EDIT_LANGS = ['es', 'en'];
  var DEFAULT_DATE = '2026-11-01';
  var MAX_CARDS = 12;
  var ROW_MAX = 6;          // tarjetas por fila; con más, se reparten en dos
  var CHECKS = 3;

  // Tope de caracteres de cada texto (lo que cabe en el diseño).
  var LIMITS = {
    badge: 40, dateText: 40, headline: 110, subhead: 110,
    cardTitle: 26, cardText: 34, cardBack: 120,
    ctaKicker: 60, ctaTitle: 48, ctaText: 200, ctaButton: 28, check: 40
  };

  // Textos sueltos: clave de i18n de la app + valor de fábrica (es / en).
  var TEXTS = {
    badge: { key: 'agencies_launch_badge', limit: 'badge', es: 'Lanzamiento oficial', en: 'Official launch' },
    headline: { key: 'agencies_launch_headline', limit: 'headline', es: 'Una nueva era para las agencias de TikTok LIVE.', en: 'A new era for TikTok LIVE agencies.' },
    subhead: { key: 'agencies_launch_subhead', limit: 'subhead', es: 'Gestiona, impulsa y posiciona nuevos creadores desde TikControl.', en: 'Manage, grow and position new creators from TikControl.' },
    ctaKicker: { key: 'agencies_launch_cta_kicker', limit: 'ctaKicker', es: '¿Aún no te has reunido con nosotros?', en: "Haven't met with us yet?" },
    ctaTitle: { key: 'agencies_launch_cta_title', limit: 'ctaTitle', es: 'Agenda ahora tu llamada', en: 'Book your call now' },
    ctaText: { key: 'agencies_launch_cta_text', limit: 'ctaText', es: 'Descubre cómo TikControl Agencies puede ayudar a tu agencia a crecer y llevar la gestión de tus creadores al siguiente nivel.', en: 'Find out how TikControl Agencies can help your agency grow and take the management of your creators to the next level.' },
    ctaButton: { key: 'agencies_launch_cta_button', limit: 'ctaButton', es: 'Agendar llamada', en: 'Book a call' }
  };

  // Las tres ventajas de debajo del botón.
  var CHECK_TEXTS = [
    { key: 'agencies_launch_check_1', es: 'Asesoramiento personalizado', en: 'Personalized advice' },
    { key: 'agencies_launch_check_2', es: 'Sin compromiso', en: 'No commitment' },
    { key: 'agencies_launch_check_3', es: 'Conoce todas las posibilidades', en: 'See everything it can do' }
  ];

  // Tarjetas de fábrica: [título, texto, dorso]. El dorso es el texto más largo que
  // sale al girarse la tarjeta con el ratón encima. `id` las une a su traducción
  // (agencies_launch_card_<id>_t / _d / _b).
  var CARDS = [
    { id: 'creators', icon: 'creators', es: ['Creadores', 'Gestiona tu talento', 'Toda tu cartera en un solo panel: niveles, diamantes y actividad de cada creador.'], en: ['Creators', 'Manage your talent', 'Your whole roster in one panel: levels, diamonds and activity for every creator.'] },
    { id: 'licenses', icon: 'licenses', es: ['Licencias', 'Control y asignación', 'Asigna licencias Premium a tus creadores y controla tus créditos en todo momento.'], en: ['Licenses', 'Control and assignment', 'Assign Premium licenses to your creators and keep track of your credits at all times.'] },
    { id: 'missions', icon: 'missions', es: ['Misiones', 'Impulsa su crecimiento', 'Crea retos con premios y sigue el avance de cada creador en tiempo real.'], en: ['Missions', 'Boost their growth', "Create challenges with prizes and follow each creator's progress in real time."] },
    { id: 'events', icon: 'events', es: ['Eventos', 'Organiza y coordina', 'Organiza eventos y competiciones, con participantes, premios y clasificación.'], en: ['Events', 'Organize and coordinate', 'Run events and competitions with participants, prizes and leaderboards.'] },
    { id: 'profiles', icon: 'profiles', es: ['Perfiles LIVE', 'Configura sus directos', 'Prepara perfiles de directo listos para usar y compártelos con tus creadores.'], en: ['LIVE Profiles', 'Set up their streams', 'Prepare ready-to-use LIVE profiles and share them with your creators.'] },
    { id: 'analytics', icon: 'analytics', es: ['Analítica', 'Datos que importan', 'Diamantes, batallas y evolución de tu agencia, día a día.'], en: ['Analytics', 'Data that matters', "Diamonds, battles and your agency's growth, day by day."] },
    { id: 'library', icon: 'library', es: ['Biblioteca', 'Formación y recursos', 'Formaciones, creatividades y recursos para que tus creadores crezcan más rápido.'], en: ['Library', 'Training and resources', 'Training, creatives and resources to help your creators grow faster.'] },
    { id: 'animations', icon: 'animations', es: ['Animaciones LIVE', 'Packs personalizados', 'Packs de animaciones con la marca de tu agencia para los directos de tus creadores.'], en: ['LIVE Animations', 'Custom packs', "Animation packs with your agency's branding for your creators' streams."] },
    { id: 'agency', icon: 'agency', es: ['Agencia', 'Gestiona tu equipo', 'Tu equipo, sus roles y permisos, y la imagen pública de tu agencia en un solo sitio.'], en: ['Agency', 'Manage your team', "Your team, their roles and permissions, and your agency's public profile in one place."] },
    { id: 'finder', icon: 'search', es: ['Buscador de agencias', 'Recluta nuevos talentos', 'Aparece en el buscador de agencias y recibe solicitudes de nuevos creadores.'], en: ['Agency finder', 'Recruit new talent', 'Appear in the agency finder and receive applications from new creators.'] },
    { id: 'tickets', icon: 'tickets', es: ['Tickets', 'Soporte prioritario', 'Atiende las peticiones de tus creadores con seguimiento y respuesta prioritaria.'], en: ['Tickets', 'Priority support', "Handle your creators' requests with tracking and priority response."] }
  ];

  var CARD_BY_ID = {};
  CARDS.forEach(function (c) { CARD_BY_ID[c.id] = c; });

  var SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,29}$/;

  // ── Limpieza ─────────────────────────────────────────────────
  function clip(value, max) {
    return String(value == null ? '' : value).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
  }

  /** { es, en } recortado; cualquier otra cosa → vacío. */
  function pair(value, max) {
    var v = value && typeof value === 'object' ? value : {};
    return { es: clip(v.es, max), en: clip(v.en, max) };
  }

  function validDate(value) {
    var s = String(value == null ? '' : value).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
    var d = new Date(s + 'T12:00:00Z');
    return isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== s ? '' : s;
  }

  function sanitizeCard(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var id = SLUG_RE.test(String(raw.id || '')) && CARD_BY_ID[raw.id] ? String(raw.id) : '';
    var icon = SLUG_RE.test(String(raw.icon || '')) ? String(raw.icon) : (id ? CARD_BY_ID[id].icon : '');
    var title = pair(raw.title, LIMITS.cardTitle);
    var text = pair(raw.text, LIMITS.cardText);
    var back = pair(raw.back, LIMITS.cardBack);
    // Una tarjeta propia (sin la de fábrica debajo) necesita al menos un título.
    if (!id && !title.es && !title.en) return null;
    return { id: id, icon: icon, title: title, text: text, back: back };
  }

  /**
   * Deja la configuración guardada en su forma conocida. Nunca lanza: lo que
   * no entiende lo deja vacío (= valor de fábrica). `cards: null` son las
   * tarjetas de fábrica; una lista (aunque esté vacía) las sustituye.
   */
  function sanitize(raw) {
    var src = raw && typeof raw === 'object' ? raw : {};
    var texts = {};
    Object.keys(TEXTS).forEach(function (name) {
      texts[name] = pair(src.texts && src.texts[name], LIMITS[TEXTS[name].limit]);
    });
    var checks = [];
    for (var i = 0; i < CHECKS; i++) checks.push(pair(Array.isArray(src.checks) ? src.checks[i] : null, LIMITS.check));
    var cards = null;
    if (Array.isArray(src.cards)) {
      cards = [];
      var seen = {};
      for (var j = 0; j < src.cards.length && cards.length < MAX_CARDS; j++) {
        var card = sanitizeCard(src.cards[j]);
        if (!card || (card.id && seen[card.id])) continue;   // cada tarjeta de fábrica, una vez
        if (card.id) seen[card.id] = true;
        cards.push(card);
      }
    }
    return {
      date: validDate(src.date),
      dateText: pair(src.dateText, LIMITS.dateText),
      texts: texts,
      checks: checks,
      cards: cards,
      ctaHidden: src.ctaHidden === true
    };
  }

  /** La lista de fábrica en el formato que se guarda (para empezar a editar). */
  function defaultCards() {
    return CARDS.map(function (c) {
      return { id: c.id, icon: c.icon, title: { es: '', en: '' }, text: { es: '', en: '' }, back: { es: '', en: '' } };
    });
  }

  // ── De la configuración a lo que se pinta ────────────────────
  function langOf(lang) {
    return String(lang || 'es').slice(0, 2).toLowerCase() || 'es';
  }

  // Lo escrito en el backoffice para ese idioma: español tal cual; el resto, el inglés.
  function own(value, lang) {
    if (!value) return '';
    return lang === 'es' ? value.es : value.en;
  }

  /** «1 de noviembre» en el idioma de la app (sin año). */
  function formatDate(iso, lang) {
    var date = new Date((validDate(iso) || DEFAULT_DATE) + 'T12:00:00Z');
    try {
      return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
    } catch (_) {
      return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
    }
  }

  // Dos filas como mucho: hasta ROW_MAX caben en una; si no, mitad y mitad
  // (la de arriba se queda la que sobra).
  function splitRows(cards) {
    if (cards.length <= ROW_MAX) return cards.length ? [cards] : [];
    var first = Math.ceil(cards.length / 2);
    return [cards.slice(0, first), cards.slice(first)];
  }

  /**
   * Lo que se pinta: textos ya elegidos para `lang`. `t(key, porDefecto)` es
   * el traductor de la app (en el backoffice no hay: salen los de fábrica).
   */
  function resolve(raw, lang, t) {
    var cfg = sanitize(raw);
    var L = langOf(lang);
    var tr = typeof t === 'function' ? t : function (_key, fallback) { return fallback; };
    var factory = function (def) { return tr(def.key, L === 'es' ? def.es : def.en); };
    var text = function (name) { return own(cfg.texts[name], L) || factory(TEXTS[name]); };

    var cards = (cfg.cards || defaultCards()).map(function (card) {
      var def = CARD_BY_ID[card.id];
      var title = own(card.title, L);
      var desc = own(card.text, L);
      var back = own(card.back, L);
      if (def) {
        // Cada campo sin escribir cae a su texto de fábrica.
        if (!title) title = tr('agencies_launch_card_' + def.id + '_t', L === 'es' ? def.es[0] : def.en[0]);
        if (!desc) desc = tr('agencies_launch_card_' + def.id + '_d', L === 'es' ? def.es[1] : def.en[1]);
        if (!back) back = tr('agencies_launch_card_' + def.id + '_b', L === 'es' ? def.es[2] : def.en[2]);
      } else {
        // Tarjeta propia: solo existe en los idiomas del backoffice.
        if (!title) title = card.title.en || card.title.es;
        if (!desc) desc = card.text.en || card.text.es;
        if (!back) back = card.back.en || card.back.es;   // sin dorso, la tarjeta no se gira
      }
      return { icon: card.icon || (def ? def.icon : ''), title: title, text: desc, back: back };
    }).filter(function (card) { return !!card.title; });

    return {
      badge: text('badge'),
      date: own(cfg.dateText, L) || formatDate(cfg.date, L),
      headline: text('headline'),
      subhead: text('subhead'),
      cards: cards,
      rows: splitRows(cards),
      cta: cfg.ctaHidden ? null : {
        kicker: text('ctaKicker'),
        title: text('ctaTitle'),
        text: text('ctaText'),
        button: text('ctaButton'),
        checks: cfg.checks.map(function (c, i) { return own(c, L) || factory(CHECK_TEXTS[i]); })
      }
    };
  }

  /** Todas las claves de i18n de fábrica con su texto en es/en (lo vigila el test de locales). */
  function i18nDefaults() {
    var out = {};
    Object.keys(TEXTS).forEach(function (name) { out[TEXTS[name].key] = { es: TEXTS[name].es, en: TEXTS[name].en }; });
    CHECK_TEXTS.forEach(function (c) { out[c.key] = { es: c.es, en: c.en }; });
    CARDS.forEach(function (c) {
      out['agencies_launch_card_' + c.id + '_t'] = { es: c.es[0], en: c.en[0] };
      out['agencies_launch_card_' + c.id + '_d'] = { es: c.es[1], en: c.en[1] };
      out['agencies_launch_card_' + c.id + '_b'] = { es: c.es[2], en: c.en[2] };
    });
    return out;
  }

  var api = {
    EDIT_LANGS: EDIT_LANGS,
    DEFAULT_DATE: DEFAULT_DATE,
    MAX_CARDS: MAX_CARDS,
    ROW_MAX: ROW_MAX,
    LIMITS: LIMITS,
    TEXTS: TEXTS,
    CHECK_TEXTS: CHECK_TEXTS,
    CARDS: CARDS,
    sanitize: sanitize,
    defaultCards: defaultCards,
    formatDate: formatDate,
    splitRows: splitRows,
    resolve: resolve,
    i18nDefaults: i18nDefaults
  };

  root.AgenciasLaunchContent = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
